import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FunnelIcon,
  ArrowPathIcon,
  XMarkIcon,
  EyeIcon,
  BanknotesIcon,
  CheckCircleIcon,
  CreditCardIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import { walletApi, PaymentTransaction } from '@/api/walletApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  REFUNDED: 'bg-purple-100 text-purple-800',
  PARTIALLY_REFUNDED: 'bg-orange-100 text-orange-800',
}

const typeColors: Record<string, string> = {
  INVOICE_PAYMENT: 'bg-blue-100 text-blue-800',
  WALLET_TOPUP: 'bg-green-100 text-green-800',
  WALLET_WITHDRAWAL: 'bg-red-100 text-red-800',
  REFUND: 'bg-purple-100 text-purple-800',
  TRANSFER: 'bg-cyan-100 text-cyan-800',
  FEE_PAYMENT: 'bg-indigo-100 text-indigo-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

const paymentMethodColors: Record<string, string> = {
  MPESA: 'bg-emerald-100 text-emerald-800',
  MPESA_PAYBILL: 'bg-emerald-100 text-emerald-800',
  BANK_TRANSFER: 'bg-blue-100 text-blue-800',
  CARD: 'bg-purple-100 text-purple-800',
  CASH: 'bg-green-100 text-green-800',
  CHEQUE: 'bg-orange-100 text-orange-800',
  WALLET: 'bg-cyan-100 text-cyan-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type TransactionType = 'INVOICE_PAYMENT' | 'WALLET_TOPUP' | 'WALLET_WITHDRAWAL' | 'REFUND' | 'TRANSFER' | 'FEE_PAYMENT' | 'OTHER'
type TransactionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'REFUNDED' | 'PARTIALLY_REFUNDED'
type PaymentMethodType = 'MPESA' | 'MPESA_PAYBILL' | 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'CHEQUE' | 'WALLET' | 'OTHER'

export default function TransactionsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<{
    status?: TransactionStatus
    type?: TransactionType
    paymentMethod?: PaymentMethodType
    startDate?: string
    endDate?: string
  }>({})
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransaction | null>(null)

  // Fetch payment transactions from Transaction model
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['paymentTransactions', page, filters],
    queryFn: () =>
      walletApi.getPaymentTransactions({
        page,
        limit: 20,
        status: filters.status || 'COMPLETED',
        type: filters.type || undefined,
        paymentMethod: filters.paymentMethod || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
  })

  const clearFilters = () => {
    setFilters({})
    setPage(1)
  }

  const transactions = data?.data?.transactions || []
  const pagination = data?.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
  const summary = data?.data?.summary

  // Get totals from summary
  const totals = summary?.totals || { totalAmount: 0, totalCount: 0, completedAmount: 0, completedCount: 0 }
  const byMethod = summary?.byMethod || []

  // Get M-Pesa total
  const mpesaStats = byMethod.find((m) => m._id === 'MPESA' || m._id === 'MPESA_PAYBILL') || { total: 0, count: 0 }
  const cashStats = byMethod.find((m) => m._id === 'CASH') || { total: 0, count: 0 }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Transactions</h1>
          <p className="text-gray-600">View all completed payment transactions</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed Amount</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.completedAmount)}</p>
              <p className="text-xs text-gray-500">{totals.completedCount} transactions</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{totals.totalCount}</p>
              <p className="text-xs text-gray-500">{formatCurrency(totals.totalAmount)} total</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">M-Pesa</p>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(mpesaStats.total)}</p>
              <p className="text-xs text-gray-500">{mpesaStats.count} payments</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <CreditCardIcon className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Cash</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(cashStats.total)}</p>
              <p className="text-xs text-gray-500">{cashStats.count} payments</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <BanknotesIcon className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filters
          </button>
          {Object.keys(filters).length > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700"
            >
              <XMarkIcon className="w-5 h-5" />
              Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status || 'COMPLETED'}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as TransactionStatus || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Status</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="FAILED">Failed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="REFUNDED">Refunded</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as TransactionType || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Types</option>
                <option value="INVOICE_PAYMENT">Invoice Payment</option>
                <option value="WALLET_TOPUP">Wallet Top-up</option>
                <option value="WALLET_WITHDRAWAL">Withdrawal</option>
                <option value="FEE_PAYMENT">Fee Payment</option>
                <option value="REFUND">Refund</option>
                <option value="TRANSFER">Transfer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={filters.paymentMethod || ''}
                onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value as PaymentMethodType || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Methods</option>
                <option value="MPESA">M-Pesa</option>
                <option value="CASH">Cash</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CARD">Card</option>
                <option value="CHEQUE">Cheque</option>
                <option value="WALLET">Wallet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12">
            <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No transactions found</p>
            <p className="text-sm text-gray-400 mt-1">Completed transactions will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student/User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((txn: PaymentTransaction) => {
                  const user = typeof txn.userId === 'object' ? txn.userId : null
                  const student = typeof txn.studentId === 'object' ? txn.studentId : null
                  const invoice = typeof txn.invoiceId === 'object' ? txn.invoiceId : null

                  return (
                    <tr key={txn._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(txn.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-mono text-gray-900">{txn.transactionRef}</p>
                        {invoice && (
                          <p className="text-xs text-gray-500">Invoice: {invoice.invoiceNumber}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[txn.type] || typeColors.OTHER}`}>
                          {txn.type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentMethodColors[txn.paymentMethod] || paymentMethodColors.OTHER}`}>
                          {txn.paymentMethod?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-green-600">
                          {formatCurrency(txn.amount, txn.currency)}
                        </p>
                        {txn.fee ? (
                          <p className="text-xs text-gray-500">Fee: {formatCurrency(txn.fee)}</p>
                        ) : null}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusColors[txn.status]}`}>
                          {txn.status === 'COMPLETED' && <CheckCircleIcon className="w-3 h-3" />}
                          {txn.status === 'PENDING' && <ClockIcon className="w-3 h-3" />}
                          {txn.status === 'FAILED' && <XCircleIcon className="w-3 h-3" />}
                          {txn.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {student ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {student.firstName} {student.lastName}
                            </p>
                            <p className="text-xs text-gray-500">Student</p>
                          </div>
                        ) : user ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedTransaction(txn)}
                          className="p-1 text-gray-500 hover:text-primary-600"
                          title="View details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {(page - 1) * pagination.limit + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} transactions
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.pages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Transaction Details</h3>
              <button onClick={() => setSelectedTransaction(null)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Amount Header */}
              <div className={`p-4 rounded-lg ${selectedTransaction.status === 'COMPLETED' ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  {selectedTransaction.status === 'COMPLETED' ? (
                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                  ) : selectedTransaction.status === 'FAILED' ? (
                    <XCircleIcon className="w-8 h-8 text-red-600" />
                  ) : (
                    <ClockIcon className="w-8 h-8 text-yellow-600" />
                  )}
                  <div>
                    <p className={`text-sm ${selectedTransaction.status === 'COMPLETED' ? 'text-green-700' : 'text-gray-700'}`}>
                      {selectedTransaction.type?.replace(/_/g, ' ')}
                    </p>
                    <p className={`text-2xl font-bold ${selectedTransaction.status === 'COMPLETED' ? 'text-green-800' : 'text-gray-800'}`}>
                      {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Transaction Reference</p>
                  <p className="font-mono text-sm">{selectedTransaction.transactionRef}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[selectedTransaction.status]}`}>
                    {selectedTransaction.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentMethodColors[selectedTransaction.paymentMethod]}`}>
                    {selectedTransaction.paymentMethod?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Gateway</p>
                  <p className="font-medium">{selectedTransaction.paymentGateway}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Initiated At</p>
                  <p className="font-medium">{formatDate(selectedTransaction.initiatedAt)}</p>
                </div>
                {selectedTransaction.completedAt && (
                  <div>
                    <p className="text-sm text-gray-500">Completed At</p>
                    <p className="font-medium">{formatDate(selectedTransaction.completedAt)}</p>
                  </div>
                )}
                {selectedTransaction.fee && selectedTransaction.fee > 0 && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500">Fee</p>
                      <p className="font-medium">{formatCurrency(selectedTransaction.fee)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Net Amount</p>
                      <p className="font-medium">{formatCurrency(selectedTransaction.netAmount || selectedTransaction.amount - selectedTransaction.fee)}</p>
                    </div>
                  </>
                )}
              </div>

              {selectedTransaction.description && (
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-gray-900">{selectedTransaction.description}</p>
                </div>
              )}

              {/* M-Pesa Details */}
              {selectedTransaction.mpesaDetails && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">M-Pesa Details</p>
                  <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                    {selectedTransaction.mpesaDetails.phoneNumber && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-700">Phone Number</span>
                        <span className="font-mono">{selectedTransaction.mpesaDetails.phoneNumber}</span>
                      </div>
                    )}
                    {selectedTransaction.mpesaDetails.mpesaReceiptNumber && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-700">Receipt Number</span>
                        <span className="font-mono font-bold">{selectedTransaction.mpesaDetails.mpesaReceiptNumber}</span>
                      </div>
                    )}
                    {selectedTransaction.mpesaDetails.accountReference && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-700">Account Reference</span>
                        <span className="font-mono">{selectedTransaction.mpesaDetails.accountReference}</span>
                      </div>
                    )}
                    {selectedTransaction.mpesaDetails.resultDesc && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-700">Result</span>
                        <span>{selectedTransaction.mpesaDetails.resultDesc}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Info */}
              {typeof selectedTransaction.invoiceId === 'object' && selectedTransaction.invoiceId && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <DocumentTextIcon className="w-5 h-5 text-gray-500" />
                    <p className="text-sm font-medium text-gray-700">Related Invoice</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-mono font-medium">{selectedTransaction.invoiceId.invoiceNumber}</p>
                        <p className="text-sm text-blue-700">{selectedTransaction.invoiceId.title}</p>
                      </div>
                      <p className="text-lg font-bold text-blue-800">
                        {formatCurrency(selectedTransaction.invoiceId.grandTotal)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* User/Student Info */}
              {(typeof selectedTransaction.studentId === 'object' && selectedTransaction.studentId) && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Student</p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-medium">
                      {selectedTransaction.studentId.firstName} {selectedTransaction.studentId.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{selectedTransaction.studentId.email}</p>
                  </div>
                </div>
              )}

              {(typeof selectedTransaction.userId === 'object' && selectedTransaction.userId) && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500 mb-2">Paid By</p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="font-medium">
                      {selectedTransaction.userId.firstName} {selectedTransaction.userId.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{selectedTransaction.userId.email}</p>
                  </div>
                </div>
              )}

              {selectedTransaction.failureReason && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-red-600 font-medium">Failure Reason</p>
                  <p className="text-red-700 bg-red-50 rounded-lg p-3 mt-1">{selectedTransaction.failureReason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
